/**
 * Theme Drawer Component v3.0.0
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * ES Module drawer web component with drag-to-close support.
 * Extends Component base class for AbortController and lifecycle management.
 *
 * Features:
 * - Drag-to-close with velocity detection
 * - Direction: left, right, bottom, top
 * - Focus trap and escape key
 * - Body scroll lock
 * - Alpine store sync (optional)
 *
 * Usage:
 * <theme-drawer id="cart-drawer" data-direction="right">
 *   <div data-drawer-overlay></div>
 *   <div data-drawer-content>
 *     <button data-drawer-close>Close</button>
 *     Content here...
 *   </div>
 * </theme-drawer>
 *
 * API:
 * - drawer.open()
 * - drawer.close()
 * - drawer.toggle()
 * - drawer.isOpen
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { Component } from './component-base.js';

// Get Logger from window.Theme (set by theme-core.js IIFE)
const Logger = window.Theme?.Logger || {
  ns: () => ({
    debug: () => {},
    info: () => {},
    warn: console.warn.bind(console),
    error: console.error.bind(console)
  })
};

const log = Logger.ns('drawer');

// Constants
const VELOCITY_THRESHOLD = 0.4; // px/ms

/**
 * Theme Drawer Web Component
 * @extends Component
 *
 * State Model (Horizon pattern):
 * - data-open attribute IS the source of truth (like <dialog>.open)
 * - isOpen getter/setter reflects the attribute
 * - Alpine store sync still works via effect in alpine-stores.js
 */
export class ThemeDrawer extends Component {
  // Private fields (no #isOpen - attribute is source of truth)
  #isDragging = false;
  #dragStart = null;
  #dragCurrent = null;
  #dragStartTime = null;
  #previousActiveElement = null;

  // Cached elements
  #overlay = null;
  #content = null;
  #handle = null;

  // Configuration
  #direction = 'right';
  #closeThreshold = 0.25;
  #dismissible = true;

  connectedCallback() {
    super.connectedCallback();

    // Cache elements
    this.#overlay = this.querySelector('[data-drawer-overlay]');
    this.#content = this.querySelector('[data-drawer-content]');
    this.#handle = this.querySelector('[data-drawer-handle]');

    if (!this.#content) {
      log.warn('Missing [data-drawer-content] element');
      return;
    }

    // Read configuration
    this.#direction = this.getAttribute('data-direction') || 'right';
    this.#closeThreshold = parseFloat(this.getAttribute('data-close-threshold')) || 0.25;
    this.#dismissible = this.getAttribute('data-dismissible') !== 'false';

    // Setup
    this.#setupEvents();
    this.#setupARIA();
    // NOTE: Alpine store watching is handled by Alpine.effect() in alpine-stores.js
    // The effect bridges $store.ui.cartOpen → drawer.open()/close()
    // This keeps the web component self-contained (no Alpine knowledge needed)
    // Focus trap handled by Alpine Focus plugin (x-trap directive on content)

    log.debug('Connected', { direction: this.#direction, isOpen: this.isOpen });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.isOpen) {
      document.body.style.overflow = '';
    }
    log.debug('Disconnected');
  }

  // ─── Setup ───────────────────────────────────────────────────────────────

  #setupARIA() {
    this.#content.setAttribute('role', 'dialog');
    this.#content.setAttribute('aria-modal', 'true');
    if (!this.isOpen) {
      this.#content.setAttribute('aria-hidden', 'true');
    }
  }

  #setupEvents() {
    const { signal } = this.signal;

    // Overlay click
    this.#overlay?.addEventListener('click', () => this.close(), { signal });

    // Close buttons
    this.querySelectorAll('[data-drawer-close]').forEach(btn => {
      btn.addEventListener('click', (ev) => {
        ev.preventDefault();
        this.close();
      }, { signal });
    });

    // Escape key
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && this.isOpen) {
        ev.preventDefault();
        this.close();
      }
    }, { signal });

    // Drag events
    if (this.#dismissible) {
      this.#setupDragEvents();
    }
  }

  #setupDragEvents() {
    const { signal } = this.signal;
    const dragTarget = this.#handle || this.#content;

    dragTarget.addEventListener('pointerdown', (ev) => {
      if (ev.button !== 0) return;
      if (ev.target.closest('button, a, input, select, textarea')) return;
      this.#startDrag(ev);
    }, { signal });

    document.addEventListener('pointermove', (ev) => {
      if (!this.#isDragging) return;
      this.#onDrag(ev);
    }, { signal });

    document.addEventListener('pointerup', (ev) => {
      if (!this.#isDragging) return;
      this.#endDrag(ev);
    }, { signal });

    document.addEventListener('pointercancel', (ev) => {
      if (!this.#isDragging) return;
      this.#endDrag(ev);
    }, { signal });
  }

  // ─── Drag Handling ───────────────────────────────────────────────────────

  #startDrag(ev) {
    this.#isDragging = true;
    this.#dragStart = { x: ev.clientX, y: ev.clientY };
    this.#dragCurrent = this.#dragStart;
    this.#dragStartTime = Date.now();

    this.#content.setPointerCapture?.(ev.pointerId);
    this.#content.style.transition = 'none';

    log.debug('Drag started', this.#dragStart);
  }

  #onDrag(ev) {
    this.#dragCurrent = { x: ev.clientX, y: ev.clientY };
    const delta = this.#getDragDelta();
    const dampened = this.#dampenDelta(delta);

    this.#applyDragTransform(dampened);

    if (this.#overlay) {
      const progress = Math.min(1, Math.abs(delta.main) / this.#getDrawerSize());
      this.#overlay.style.opacity = String(1 - progress * 0.5);
    }
  }

  #endDrag() {
    const delta = this.#getDragDelta();
    const velocity = this.#getDragVelocity();
    const size = this.#getDrawerSize();
    const progress = Math.abs(delta.main) / size;

    this.#isDragging = false;
    this.#content.style.transition = '';
    this.#content.style.transform = '';

    if (this.#overlay) {
      this.#overlay.style.opacity = '';
    }

    // Determine if should close
    const shouldClose =
      (Math.abs(velocity) > VELOCITY_THRESHOLD && this.#isClosingDirection(delta)) ||
      (progress > this.#closeThreshold && this.#isClosingDirection(delta));

    if (shouldClose) {
      this.close();
    }

    log.debug('Drag ended', { shouldClose, progress, velocity });
  }

  #getDragDelta() {
    const dx = this.#dragCurrent.x - this.#dragStart.x;
    const dy = this.#dragCurrent.y - this.#dragStart.y;
    const isHorizontal = this.#direction === 'left' || this.#direction === 'right';

    return {
      x: dx,
      y: dy,
      main: isHorizontal ? dx : dy,
      cross: isHorizontal ? dy : dx
    };
  }

  #getDragVelocity() {
    const delta = this.#getDragDelta();
    const elapsed = Date.now() - this.#dragStartTime;
    return elapsed > 0 ? delta.main / elapsed : 0;
  }

  #isClosingDirection(delta) {
    switch (this.#direction) {
      case 'right': return delta.main > 0;
      case 'left': return delta.main < 0;
      case 'bottom': return delta.main > 0;
      case 'top': return delta.main < 0;
      default: return false;
    }
  }

  #getDrawerSize() {
    const rect = this.#content.getBoundingClientRect();
    const isHorizontal = this.#direction === 'left' || this.#direction === 'right';
    return isHorizontal ? rect.width : rect.height;
  }

  #dampenDelta(delta) {
    const dampened = { ...delta };
    if (!this.#isClosingDirection(delta)) {
      dampened.main = delta.main * 0.2;
    }
    return dampened;
  }

  #applyDragTransform(delta) {
    let transform = '';

    switch (this.#direction) {
      case 'right':
        transform = `translateX(${Math.max(0, delta.main)}px)`;
        break;
      case 'left':
        transform = `translateX(${Math.min(0, delta.main)}px)`;
        break;
      case 'bottom':
        transform = `translateY(${Math.max(0, delta.main)}px)`;
        break;
      case 'top':
        transform = `translateY(${Math.min(0, delta.main)}px)`;
        break;
    }

    this.#content.style.transform = transform;
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  /**
   * isOpen - attribute-based state (Horizon pattern)
   * The data-open attribute IS the source of truth.
   */
  get isOpen() {
    return this.hasAttribute('data-open');
  }

  set isOpen(value) {
    if (value) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    if (this.isOpen) return;

    this.#previousActiveElement = document.activeElement;

    // Set attribute (source of truth)
    this.setAttribute('data-open', '');
    this.#content.setAttribute('aria-hidden', 'false');

    // Scroll lock
    document.body.style.overflow = 'hidden';

    // Focus management
    requestAnimationFrame(() => {
      const focusable = this.#content.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable ? focusable.focus() : this.#content.focus();
    });

    // Sync Alpine store
    this.#syncAlpineStore(true);

    log.debug('Opened');
  }

  close() {
    if (!this.isOpen) return;

    // Remove attribute (source of truth)
    this.removeAttribute('data-open');
    this.#content.setAttribute('aria-hidden', 'true');

    // Restore scroll
    document.body.style.overflow = '';

    // Restore focus
    this.#previousActiveElement?.focus?.();

    // Sync Alpine store
    this.#syncAlpineStore(false);

    log.debug('Closed');
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  // ─── Alpine Integration ──────────────────────────────────────────────────

  /**
   * Sync drawer state back to Alpine store
   * Called after open()/close() to keep store in sync
   */
  #syncAlpineStore(isOpen) {
    if (typeof Alpine === 'undefined' || !Alpine.store) return;

    const ui = Alpine.store('ui');
    if (!ui) return;

    const id = this.id || '';
    let storeKey = null;

    if (id.includes('cart')) storeKey = 'cartOpen';
    else if (id.includes('menu')) storeKey = 'menuOpen';
    else if (id.includes('search')) storeKey = 'searchMobileOpen';

    if (storeKey && storeKey in ui && ui[storeKey] !== isOpen) {
      ui[storeKey] = isOpen;
      log.debug('Synced to Alpine store', { key: storeKey, value: isOpen });
    }
  }
}

// Register component
if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
  log.info('Registered');
}
